import React, { useState, useEffect } from "react";
import {
  Package,
  Search,
  RefreshCw,
  Plus,
  Eye,
  Edit,
  Trash2,
  Filter,
  Upload,
  X,
  Loader,
  Image as ImageIcon,
  Globe,
  DollarSign,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
  Tag,
  Layers,
} from "lucide-react";
import { Product, ProductVariant, FilterState, IMAGE_BASE_URL } from "./types";
import { api } from "../../services/api";
import BulkImportModal from "./BulkImportModal";
import SimpleBulkImportModal from "./SimpleBulkImportModal";
import EditProductModal from "./EditProductModal";

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ProductsProps {
  products: (Product | ProductVariant)[];
  loading: boolean;
  error: string | null;
  filters: FilterState;
  pagination: PaginationInfo;
  onRefresh: () => void;
  onFilterChange: (filters: Partial<FilterState>) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const Products: React.FC<ProductsProps> = ({
  products,
  loading,
  error,
  filters,
  pagination,
  onRefresh,
  onFilterChange,
  onPageChange,
  onLimitChange,
}) => {
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showSimpleBulkImport, setShowSimpleBulkImport] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<Product | ProductVariant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Use products directly since filtering is done on backend
  const filteredProducts = products;
  
  // Helper function to get product name (handles both Product and ProductVariant)
  const getProductName = (item: any) => {
    return item.variantName || item.product_name || item.baseProductName || "Unknown";
  };
  
  // Helper function to get SKU (handles both Product and ProductVariant)
  const getProductSKU = (item: any) => {
    return item.variantSKU || item.sku || "—";
  };
  
  // Helper function to get product ID (handles both Product and ProductVariant)
  const getProductId = (item: any) => {
    return item._id || item.product_id || "";
  };

  // Get unique categories and manufacturers for filters
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [allManufacturers, setAllManufacturers] = useState<string[]>([]);

  useEffect(() => {
    // Fetch filter options from backend
    const loadFilterOptions = async () => {
      try {
        const response = await api.getProductFilterOptions();
        if (response.success && response.data) {
          setAllCategories(response.data.categories || []);
          setAllManufacturers(response.data.manufacturers || []);
        }
      } catch (error) {
        console.error("Error loading filter options:", error);
      }
    };
    
    loadFilterOptions();
  }, []);

  const categories = allCategories;
  const manufacturers = allManufacturers;

  const handleAdd = () => {
    setEditingProductId(undefined);
    setShowEditModal(true);
  };

  const handleEdit = (product: Product | ProductVariant) => {
    const productId = getProductId(product);
    if (productId) {
      setEditingProductId(productId);
      setShowEditModal(true);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setIsDeleting(true);
    try {
      const productId = getProductId(deleteConfirm);
      if (!productId) {
        throw new Error("Product ID not found");
      }

      // Check if it's a variant (has variantName property) or a product
      const isVariant = 'variantName' in deleteConfirm || 'baseProductId' in deleteConfirm;
      
      if (isVariant) {
        await api.deleteVariant(productId);
      } else {
        await api.deleteProduct(productId);
      }
      
      // Remove from selection if it was selected
      const newSelection = new Set(selectedProducts);
      newSelection.delete(String(productId));
      setSelectedProducts(newSelection);
      setDeleteConfirm(null);
      onRefresh();
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Failed to delete: " + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;

    setIsBulkDeleting(true);
    try {
      const productIds = Array.from(selectedProducts);
      
      // Delete all products at once
      const response = await api.bulkDeleteProducts(productIds);

      if (response.success) {
        const deletedCount = response.data?.deleted || 0;
        const totalCount = response.data?.total || productIds.length;

        // Clear selection
        setSelectedProducts(new Set());
        setShowBulkDeleteConfirm(false);

        // Show results
        if (deletedCount < totalCount) {
          alert(`Deleted ${deletedCount} out of ${totalCount} product(s). Some products may not have been found.`);
        } else {
          alert(`Successfully deleted ${deletedCount} product(s).`);
        }

        onRefresh();
      } else {
        throw new Error(response.error || "Bulk delete failed");
      }
    } catch (error) {
      console.error("Error bulk deleting products:", error);
      alert("Failed to delete products: " + (error instanceof Error ? error.message : String(error)));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const toggleProductSelection = (productId: string) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      // Deselect all
      setSelectedProducts(new Set());
    } else {
      // Select all filtered products
      const allIds = filteredProducts.map((p) => {
        return getProductId(p);
      }).filter(Boolean);
      setSelectedProducts(new Set(allIds));
    }
  };


  const getImageUrl = (imagePath?: string) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    return IMAGE_BASE_URL + imagePath.replace(/^catalog\//, "");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Package className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedCount = selectedProducts.size;
  const allSelected = filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length;
  const someSelected = selectedProducts.size > 0 && selectedProducts.size < filteredProducts.length;

  return (
    <div className="space-y-6">
      {/* Bulk Actions Bar */}
      {selectedCount > 0 && (
        <div className="bg-[#264a3e] text-white px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="font-medium">
              {selectedCount} product{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedProducts(new Set())}
              className="text-sm underline hover:no-underline"
            >
              Clear selection
            </button>
          </div>
          <button
            onClick={() => setShowBulkDeleteConfirm(true)}
            disabled={isBulkDeleting}
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 rounded-md text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {isBulkDeleting ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </>
            )}
          </button>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search products..."
              value={filters.searchTerm}
              onChange={(e) =>
                onFilterChange({ searchTerm: e.target.value })
              }
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
            />
          </div>

          {/* Filters */}
          {categories.length > 0 && (
            <select
              value={filters.selectedCategory}
              onChange={(e) =>
                onFilterChange({ selectedCategory: e.target.value })
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}

          <select
            value={filters.selectedStockStatus}
            onChange={(e) =>
              onFilterChange({ selectedStockStatus: e.target.value })
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
          >
            <option value="all">All Products</option>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="low_stock">Low Stock (&lt;200)</option>
          </select>

          {manufacturers.length > 0 && (
            <select
              value={filters.selectedManufacturer}
              onChange={(e) =>
                onFilterChange({ selectedManufacturer: e.target.value })
              }
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#264a3e]"
            >
              <option value="all">All Manufacturers</option>
              {manufacturers.map((mfg) => (
                <option key={mfg} value={mfg}>
                  {mfg}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => setShowSimpleBulkImport(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import Products
          </button>
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center focus:outline-none"
                    title={allSelected ? "Deselect all" : "Select all"}
                  >
                    {allSelected ? (
                      <CheckSquare className="w-5 h-5 text-[#264a3e]" />
                    ) : someSelected ? (
                      <div className="relative">
                        <Square className="w-5 h-5 text-gray-400" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 bg-[#264a3e] rounded-sm" />
                        </div>
                      </div>
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No products found
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const imageUrl = getImageUrl(product.main_image);
                  const productId = getProductId(product);
                  const isSelected = selectedProducts.has(productId);
                  const productName = getProductName(product);
                  const productSKU = getProductSKU(product);
                  return (
                    <tr key={productId || product._id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleProductSelection(productId)}
                          className="flex items-center focus:outline-none"
                          title={isSelected ? "Deselect" : "Select"}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-[#264a3e]" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={productName}
                            className="h-12 w-12 object-cover rounded"
                            onError={(e) => {
                              // Hide image if it fails to load
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center">
                            <ImageIcon className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {productName}
                        </div>
                        {(product as ProductVariant).baseProductName && (product as ProductVariant).variantName && (
                          <div className="text-xs text-gray-400">
                            Base: {(product as ProductVariant).baseProductName}
                          </div>
                        )}
                        {product.manufacturer_name && (
                          <div className="text-sm text-gray-500">
                            {product.manufacturer_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.type === "bundle" ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Layers className="w-3 h-3 mr-1" />
                            Bundle
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Tag className="w-3 h-3 mr-1" />
                            Single
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.type === "bundle" ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {(product.price_rsd || product.price_mkd || 0).toFixed(2)} {product.currency || "RSD"}
                            </div>
                            {product.calculatedPrice && product.calculatedPrice > (product.price_rsd || product.price_mkd || 0) && (
                              <div className="text-xs text-gray-500">
                                <span className="line-through">{product.calculatedPrice.toFixed(2)}</span>
                                <span className="text-green-600 font-medium ml-1">
                                  ({((1 - (product.price_rsd || product.price_mkd || 0) / product.calculatedPrice) * 100).toFixed(0)}% off)
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {(product.price_rsd || product.price_mkd || 0).toFixed(2)} {product.currency || "RSD"}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.type === "bundle" ? (
                          <div className="text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                                (product.estimatedStock || 0) === 0
                                  ? "bg-red-100 text-red-800"
                                  : (product.estimatedStock || 0) < 200
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {(product.estimatedStock || 0) === 0 ? (
                                "Out of Stock"
                              ) : (
                                <>
                                  ~{product.estimatedStock} Estimated
                                  {(product.estimatedStock || 0) < 200 && " (Low)"}
                                </>
                              )}
                            </span>
                          </div>
                        ) : (
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                              (product.quantity || 0) === 0
                                ? "bg-red-100 text-red-800"
                                : (product.quantity || 0) < 200
                                ? "bg-orange-100 text-orange-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {(product.quantity || 0) === 0 ? (
                              "Out of Stock"
                            ) : (
                              <>
                                {product.quantity} In Stock
                                {(product.quantity || 0) < 200 && " (Low)"}
                              </>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(product)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(product)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(pagination.totalPages > 1 || pagination.total > 0) && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">
                    {pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}
                  </span> to{" "}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span> of{" "}
                  <span className="font-medium">{pagination.total}</span> products
                </div>
                
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Items per page:</label>
                  <select
                    value={pagination.limit}
                    onChange={(e) => onLimitChange(parseInt(e.target.value))}
                    disabled={loading}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#264a3e] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              
              {pagination.totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage || loading}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        disabled={loading}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          pagination.page === pageNum
                            ? "bg-[#264a3e] text-white"
                            : "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage || loading}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showBulkImport && (
        <BulkImportModal
          isOpen={showBulkImport}
          onClose={() => setShowBulkImport(false)}
          onImport={onRefresh}
        />
      )}

      {showSimpleBulkImport && (
        <SimpleBulkImportModal
          isOpen={showSimpleBulkImport}
          onClose={() => setShowSimpleBulkImport(false)}
          onImport={onRefresh}
        />
      )}

      {showEditModal && (
        <EditProductModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProductId(undefined);
          }}
          onSave={() => {
            setShowEditModal(false);
            setEditingProductId(undefined);
            onRefresh();
          }}
          productId={editingProductId}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete Product
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete "{getProductName(deleteConfirm)}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <span className="flex items-center">
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Delete {selectedCount} Product{selectedCount !== 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete {selectedCount} selected product{selectedCount !== 1 ? 's' : ''}? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  disabled={isBulkDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isBulkDeleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isBulkDeleting ? (
                    <span className="flex items-center">
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </span>
                  ) : (
                    `Delete ${selectedCount} Product${selectedCount !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;

